/**
 * Updates the recently viewed products in localStorage.
 * Can store IDs only (addProduct) or full product data (addProductWithData) for client-side rendering.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed product IDs */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {string} The key used to store viewed product data for rendering */
  static #STORAGE_KEY_DATA = 'viewedProductsData';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list (IDs only).
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  /**
   * Adds a product with full data for client-side rendering (e.g. recommendations).
   * @param {{ id: string|number, variant_id?: string|number, title?: string, handle?: string, url?: string, image?: string, price?: number, compare_at_price?: number }} data
   */
  static addProductWithData(data) {
    if (!data || data.id == null) return;
    const id = String(data.id);
    let ids = this.getProducts();
    let items = this.getProductsData();

    ids = ids.filter((i) => i !== id);
    ids.unshift(id);
    ids = ids.slice(0, this.#MAX_PRODUCTS);

    items = items.filter((i) => String(i.id) !== id);
    items.unshift({
      id,
      variant_id: data.variant_id != null ? String(data.variant_id) : '',
      title: data.title ?? '',
      handle: data.handle ?? '',
      url: data.url ?? '',
      image: data.image ?? '',
      price: data.price ?? 0,
      compare_at_price: data.compare_at_price ?? 0,
    });
    items = items.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(ids));
    localStorage.setItem(this.#STORAGE_KEY_DATA, JSON.stringify(items));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
    localStorage.removeItem(this.#STORAGE_KEY_DATA);
  }

  /**
   * Retrieves the list of recently viewed product IDs.
   * @returns {string[]} The list of viewed product IDs.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }

  /**
   * Retrieves recently viewed product data for client-side rendering (same order as getProducts).
   * @returns {{ id: string, variant_id?: string, title: string, handle: string, url: string, image: string, price: number, compare_at_price: number }[]}
   */
  static getProductsData() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY_DATA) || '[]');
  }
}
